import { useState, useEffect } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { apiFetch, norm, toMXN } from "./utils";
import { BRAND, Lbl, Inp, Slct, Btn, Card } from "./ui";
import { classifyCFDIs } from "./CFDIModule";
import logoImg from "./logo.png";

// ── Framework reutilizable de Cedulas ───────────────────────────────────────
// Todo lo de esta seccion (filtro Desde/Hasta, encabezado del PDF, boton de
// descarga) es comun a cualquier cedula futura: las cedulas especificas solo
// aportan su tabla en pantalla y una funcion buildPdf(desde,hasta) que arma
// columnas/filas para el PDF.

function formatCuentaConGuiones(codigo) {
  if (!codigo || codigo.length!==9) return codigo||"";
  return codigo.slice(0,3)+"-"+codigo.slice(3,6)+"-"+codigo.slice(6,9);
}

function loadImageDataUrl(src) {
  return fetch(src).then(function(r){return r.blob();}).then(function(blob){
    return new Promise(function(resolve, reject){
      var reader = new FileReader();
      reader.onloadend = function(){ resolve(reader.result); };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  });
}

function fileSafe(s) { return String(s||"").replace(/[^\w-]+/g,"_"); }

function generateCedulaPDF(spec) {
  var logoPromise = spec.logoDataUrl
    ? Promise.resolve(spec.logoDataUrl)
    : loadImageDataUrl(logoImg).catch(function(){ return null; });
  return logoPromise.then(function(logoData){
    var doc = new jsPDF({orientation:"landscape", unit:"pt"});
    if (logoData) doc.addImage(logoData, /^data:image\/png/i.test(logoData)?"PNG":"JPEG", 40, 26, 34, 34);
    doc.setFontSize(13); doc.setFont(undefined,"bold"); doc.setTextColor(15,44,112);
    doc.text("KOFIDES", 84, 40);
    doc.setFontSize(10); doc.setFont(undefined,"normal"); doc.setTextColor(90,90,90);
    doc.text(spec.clientName||"", 84, 55);
    doc.setFontSize(12); doc.setFont(undefined,"bold"); doc.setTextColor(15,23,42);
    doc.text(spec.codigo+" — "+spec.nombre, 40, 90);
    doc.setFontSize(10); doc.setFont(undefined,"normal"); doc.setTextColor(100,116,139);
    doc.text("Periodo: "+(spec.desde||"—")+" a "+(spec.hasta||"—"), 40, 106);

    var columnStyles = {};
    (spec.numericColumns||[]).forEach(function(idx){ columnStyles[idx] = {halign:"right"}; });

    autoTable(doc, {
      startY: 120,
      head: [spec.columns],
      body: spec.rows,
      styles: {fontSize:8, cellPadding:5},
      headStyles: {fillColor:[15,44,112], textColor:255, fontStyle:"bold", halign:"center"},
      columnStyles: columnStyles,
      margin: {left:40, right:40},
    });

    doc.save("Cedula_"+fileSafe(spec.codigo)+"_"+fileSafe(spec.clientName)+"_"+(spec.desde||"inicio")+"_"+(spec.hasta||"fin")+".pdf");
  });
}

function CedulaFrame(p) {
  var [desde, setDesde] = useState("");
  var [hasta, setHasta] = useState("");
  var [downloading, setDownloading] = useState(false);
  var [downloadError, setDownloadError] = useState("");

  function handleDownload() {
    setDownloading(true); setDownloadError("");
    var spec = p.buildPdf(desde, hasta);
    generateCedulaPDF({
      clientName: p.clientName, codigo: p.codigo, nombre: p.nombre,
      desde: desde, hasta: hasta, columns: spec.columns, rows: spec.rows,
      numericColumns: spec.numericColumns,
      logoDataUrl: p.logoDataUrl,
    }).catch(function(err){
      console.error("[CEDULA-PDF] No se pudo generar el PDF:", err);
      setDownloadError("No se pudo generar el PDF. Motivo: "+(err.message||"error desconocido"));
    }).finally(function(){ setDownloading(false); });
  }

  return (
    <div>
      <h2 style={{fontSize:16,fontWeight:800,color:"#0f172a",margin:"0 0 16px"}}>{p.codigo} — {p.nombre}</h2>
      <div style={{display:"flex",alignItems:"flex-end",gap:14,flexWrap:"wrap",marginBottom:16}}>
        <div><Lbl c="Desde"/><Inp type="date" value={desde} onChange={function(e){setDesde(e.target.value);}} sx={{maxWidth:170}}/></div>
        <div><Lbl c="Hasta"/><Inp type="date" value={hasta} onChange={function(e){setHasta(e.target.value);}} sx={{maxWidth:170}}/></div>
        <Btn onClick={handleDownload} disabled={p.pdfDisabled||downloading} sx={{opacity:(p.pdfDisabled||downloading)?0.6:1}}>{downloading?"Generando...":"Descargar PDF"}</Btn>
      </div>
      {downloadError?(
        <div style={{marginBottom:16,background:"#fef2f2",border:"1px solid #fecaca",borderRadius:10,padding:"10px 16px",fontSize:12,color:"#dc2626"}}>{downloadError}</div>
      ):null}
      {p.children(desde, hasta)}
    </div>
  );
}

// ── Configuracion (Parte 3): cuenta padre de Clientes ───────────────────────

var LOGO_MAX_WIDTH = 300;

function resizeAndCompressImage(file) {
  return new Promise(function(resolve, reject){
    var reader = new FileReader();
    reader.onload = function(ev){
      var img = new Image();
      img.onload = function(){
        var scale = img.width>LOGO_MAX_WIDTH ? LOGO_MAX_WIDTH/img.width : 1;
        var w = Math.max(1, Math.round(img.width*scale));
        var h = Math.max(1, Math.round(img.height*scale));
        var canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        var ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        var isPng = file.type==="image/png";
        resolve(isPng ? canvas.toDataURL("image/png") : canvas.toDataURL("image/jpeg", 0.8));
      };
      img.onerror = function(){ reject(new Error("El archivo no es una imagen valida.")); };
      img.src = ev.target.result;
    };
    reader.onerror = function(){ reject(new Error("No se pudo leer el archivo.")); };
    reader.readAsDataURL(file);
  });
}

function CedulasConfigModal(p) {
  var codigosConHijas = new Set(p.cuentas.map(function(c){ return c.subcuentaDe; }).filter(Boolean));
  var opciones = p.cuentas.filter(function(c){ return codigosConHijas.has(c.codigo); })
    .slice().sort(function(a,b){ return (a.codigo||"").localeCompare(b.codigo||""); });
  var [val, setVal] = useState(p.config.cuentaPadreClientes||"");
  var [valTope, setValTope] = useState(p.config.cuentaTopeClientes||p.config.cuentaPadreClientes||"");
  var [logoDataUrl, setLogoDataUrl] = useState(p.config.logoDataUrl||"");
  var [logoProcessing, setLogoProcessing] = useState(false);
  var [logoError, setLogoError] = useState("");

  function handleLogoFile(file) {
    setLogoError(""); setLogoProcessing(true);
    resizeAndCompressImage(file).then(function(dataUrl){
      setLogoDataUrl(dataUrl);
    }).catch(function(err){
      setLogoError("No se pudo procesar la imagen. Motivo: "+(err.message||"error desconocido"));
    }).finally(function(){ setLogoProcessing(false); });
  }

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:"#fff",borderRadius:14,padding:28,width:"100%",maxWidth:460,boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <h2 style={{fontSize:16,fontWeight:700,margin:0}}>Configuracion de Cedulas</h2>
          <button onClick={p.onCancel} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:"#94a3b8"}}>X</button>
        </div>
        <Lbl c="Cuenta padre de Clientes (para las filas de la cedula)"/>
        <Slct value={val} onChange={function(e){setVal(e.target.value);}}>
          <option value="">Seleccionar...</option>
          {opciones.map(function(c){return <option key={c.codigo} value={c.codigo}>{c.codigo+" — "+c.nombre}</option>;})}
        </Slct>
        <p style={{margin:"8px 0 0",fontSize:12,color:"#64748b"}}>Las cuentas cuya "Subcuenta de" sea esta cuenta se consideran cuentas de cliente. Se configura una sola vez y se reutiliza en todas las cedulas de cartera.</p>
        {opciones.length===0?(
          <p style={{margin:"8px 0 0",fontSize:12,color:"#d97706"}}>No hay cuentas marcadas como "Cuenta de mayor" distinta de "No" en el catalogo. Configura primero esa cuenta en Catalogo.</p>
        ):null}

        <div style={{marginTop:18}}>
          <Lbl c="Cuenta tope de Clientes (para la verificacion Segun Balance)"/>
          <Slct value={valTope} onChange={function(e){setValTope(e.target.value);}}>
            <option value="">Seleccionar...</option>
            {opciones.map(function(c){return <option key={c.codigo} value={c.codigo}>{c.codigo+" — "+c.nombre}</option>;})}
          </Slct>
          <p style={{margin:"8px 0 0",fontSize:12,color:"#64748b"}}>Cuenta raiz usada para sumar, de forma independiente, todas las cuentas descendientes (directas e indirectas) en la fila "Segun Balance". Normalmente es la misma que la cuenta padre, pero puede configurarse distinta si la jerarquia del catalogo lo requiere.</p>
        </div>

        <div style={{marginTop:18}}>
          <Lbl c="Logo para PDF de Cedulas (opcional)"/>
          <input type="file" accept="image/png,image/jpeg" disabled={logoProcessing} onChange={function(e){
            var f = e.target.files[0];
            e.target.value = "";
            if (f) handleLogoFile(f);
          }} style={{fontSize:12}}/>
          {logoProcessing?<p style={{margin:"6px 0 0",fontSize:12,color:"#64748b"}}>Procesando imagen...</p>:null}
          {logoError?<p style={{margin:"6px 0 0",fontSize:12,color:"#dc2626"}}>{logoError}</p>:null}
          {logoDataUrl?(
            <div style={{display:"flex",alignItems:"center",gap:10,marginTop:8}}>
              <img src={logoDataUrl} alt="Logo personalizado" style={{maxWidth:80,maxHeight:80,border:"1px solid #e2e8f0",borderRadius:6}}/>
              <button onClick={function(){setLogoDataUrl("");}} style={{background:"none",border:"none",color:"#dc2626",fontSize:12,cursor:"pointer"}}>Quitar logo</button>
            </div>
          ):(
            <p style={{margin:"6px 0 0",fontSize:12,color:"#64748b"}}>Si no se sube un logo, el PDF usara el logo por default de la plataforma. Esto no afecta el logo del resto de la plataforma.</p>
          )}
        </div>

        {p.error?<div style={{marginTop:14,background:"#fef2f2",border:"1px solid #fecaca",borderRadius:8,padding:"10px 14px",fontSize:12,color:"#dc2626"}}>{p.error}</div>:null}
        <div style={{display:"flex",gap:10,marginTop:20}}>
          <button onClick={p.onCancel} disabled={p.saving} style={{flex:1,padding:"10px 0",borderRadius:9,border:"1px solid #e2e8f0",background:"#fff",color:"#64748b",fontWeight:600,fontSize:13,cursor:"pointer"}}>Cancelar</button>
          <Btn onClick={function(){p.onSave({cuentaPadreClientes:val, cuentaTopeClientes:valTope||val, logoDataUrl:logoDataUrl||null});}} disabled={p.saving||!val||logoProcessing} sx={{flex:2,padding:"10px 0",borderRadius:9,opacity:(p.saving||!val||logoProcessing)?0.7:1}}>{p.saving?"Guardando...":"Guardar"}</Btn>
        </div>
      </div>
    </div>
  );
}

// ── Cedula AC.02: Conciliacion de CFDI's Emitidos vs Contabilidad ──────────

function round2(n) { return Math.round((Number(n)||0)*100)/100; }

function inRange(fecha, desde, hasta) {
  var d = String(fecha||"").split("T")[0];
  if (!d) return false;
  if (desde && d<desde) return false;
  if (hasta && d>hasta) return false;
  return true;
}

function findDescendantCodes(rootCodigo, cuentas) {
  var visited = {};
  var codigos = {};
  function visit(codigo) {
    if (visited[codigo]) return;
    visited[codigo] = true;
    cuentas.forEach(function(c){
      if (c.subcuentaDe===codigo && !visited[c.codigo]) {
        codigos[c.codigo] = true;
        visit(c.codigo);
      }
    });
  }
  visit(rootCodigo);
  return codigos;
}

function computeAC02(cuentas, movimientos, cfdis, cuentaPadre, cuentaTope, desde, hasta) {
  var cuentasCliente = cuentas.filter(function(c){ return c.subcuentaDe===cuentaPadre; })
    .slice().sort(function(a,b){ return (a.nombre||"").localeCompare(b.nombre||""); });

  var ingresos = cfdis.filter(function(f){ return f.categoria==="ingresos"; });
  var matchedUuidsEnPeriodo = {};

  var filas = cuentasCliente.map(function(c){
    var rfcManualNorm = c.rfcManual ? norm(c.rfcManual) : "";
    var matchedHist, flag, rfcMostrado;

    if (rfcManualNorm) {
      // RFC asignado manualmente: emparejamiento exacto por RFC, ignora el nombre.
      matchedHist = ingresos.filter(function(f){ return norm(f.rfcReceptor||"")===rfcManualNorm; });
      flag = matchedHist.length===0 ? "rfc_manual_sin_cfdis" : null;
      rfcMostrado = c.rfcManual;
    } else {
      var nombreNorm = norm(c.nombre);
      matchedHist = ingresos.filter(function(f){ return norm(f.nombreReceptor||"")===nombreNorm; });
      var rfcsDistintos = [];
      matchedHist.forEach(function(f){
        var r = norm(f.rfcReceptor||"");
        if (r && rfcsDistintos.indexOf(r)===-1) rfcsDistintos.push(r);
      });
      flag = null;
      if (matchedHist.length===0 || rfcsDistintos.length===0) flag = "sin_rfc";
      else if (rfcsDistintos.length>1) flag = "rfc_ambiguo";

      rfcMostrado = null;
      if (!flag) {
        var primero = matchedHist.filter(function(f){ return norm(f.rfcReceptor||"")===rfcsDistintos[0]; })[0];
        rfcMostrado = primero ? primero.rfcReceptor : "";
      }
    }

    var matchedPeriodo = matchedHist.filter(function(f){ return inRange(f.fecha, desde, hasta); });
    matchedPeriodo.forEach(function(f){ matchedUuidsEnPeriodo[f.uuid]=true; });

    var sat = round2(matchedPeriodo.reduce(function(s,f){ return s+(f.total||0); },0));
    var contab = round2(movimientos.filter(function(m){ return m.codigoCuenta===c.codigo && inRange(m.fecha, desde, hasta); }).reduce(function(s,m){ return s+(m.cargos||0); },0));

    return {
      codigo: c.codigo, nombre: c.nombre, rfc: rfcMostrado, flag: flag,
      rfcManual: c.rfcManual||"",
      contab: contab, sat: sat, variacion: round2(contab-sat),
    };
  });

  var totalCedula = {
    contab: round2(filas.reduce(function(s,f){return s+f.contab;},0)),
    sat: round2(filas.reduce(function(s,f){return s+f.sat;},0)),
    variacion: round2(filas.reduce(function(s,f){return s+f.variacion;},0)),
  };

  // "Segun Balance": verificacion independiente, no derivada de la suma de filas.
  var codigosTope = findDescendantCodes(cuentaTope, cuentas);
  var segunBalanceContab = round2(movimientos.filter(function(m){ return codigosTope[m.codigoCuenta] && inRange(m.fecha, desde, hasta); }).reduce(function(s,m){ return s+(m.cargos||0); },0));
  var segunBalanceSat = round2(ingresos.filter(function(f){ return inRange(f.fecha, desde, hasta); }).reduce(function(s,f){ return s+(f.total||0); },0));
  var segunBalance = {contab:segunBalanceContab, sat:segunBalanceSat, variacion:round2(segunBalanceContab-segunBalanceSat)};

  var variacionFinal = {
    contab: round2(totalCedula.contab-segunBalance.contab),
    sat: round2(totalCedula.sat-segunBalance.sat),
    variacion: round2(totalCedula.variacion-segunBalance.variacion),
  };

  var sinCuentaCliente = ingresos.filter(function(f){ return inRange(f.fecha, desde, hasta) && !matchedUuidsEnPeriodo[f.uuid]; });
  var sinCuentaClienteTotal = round2(sinCuentaCliente.reduce(function(s,f){return s+(f.subtotal||0);},0));

  return {
    cuentasCliente: cuentasCliente, filas: filas, totalCedula: totalCedula,
    segunBalance: segunBalance, variacionFinal: variacionFinal,
    sinCuentaCliente: sinCuentaCliente, sinCuentaClienteTotal: sinCuentaClienteTotal,
  };
}

var thSx = {padding:"10px 12px",textAlign:"left",color:"#64748b",fontWeight:600,fontSize:11};
var thRightSx = Object.assign({},thSx,{textAlign:"right"});
var tdSx = {padding:"8px 12px",color:"#374151"};
var tdRightSx = Object.assign({},tdSx,{textAlign:"right",whiteSpace:"nowrap"});
var tdBoldSx = Object.assign({},tdSx,{fontWeight:700,color:"#0f172a"});
var tdRightBoldSx = Object.assign({},tdRightSx,{fontWeight:700,color:"#0f172a"});
var warnBadgeSx = {fontSize:10,padding:"2px 8px",borderRadius:10,background:"#fef2f2",color:"#dc2626",fontWeight:700};
var warnBadgeSmallSx = {fontSize:9,padding:"1px 6px",borderRadius:10,background:"#fef2f2",color:"#dc2626",fontWeight:700,whiteSpace:"nowrap"};

function ac02FlagLabel(flag) {
  if (flag==="sin_rfc") return "Sin RFC identificado";
  if (flag==="rfc_ambiguo") return "RFC ambiguo";
  if (flag==="rfc_manual_sin_cfdis") return "RFC asignado sin CFDIs en el periodo/historico";
  return null;
}

function AC02RfcCell(p) {
  var f = p.f;
  var [editing, setEditing] = useState(false);
  var [val, setVal] = useState(f.rfcManual||"");
  var [saving, setSaving] = useState(false);
  var [err, setErr] = useState("");

  if (!editing) {
    var label = ac02FlagLabel(f.flag);
    var rfcConAdvertencia = f.flag==="rfc_manual_sin_cfdis";
    return (
      <div style={{display:"flex",alignItems:"center",gap:6}}>
        {rfcConAdvertencia ? (
          <span style={{display:"flex",alignItems:"center",gap:5}}>
            <span>{f.rfc}</span>
            <span style={warnBadgeSmallSx} title={label}>sin CFDIs</span>
          </span>
        ) : (
          <span>{label?<span style={warnBadgeSx}>{label}</span>:f.rfc}</span>
        )}
        <button onClick={function(){ setVal(f.rfcManual||""); setErr(""); setEditing(true); }} title="Editar RFC manual" style={{background:"none",border:"none",cursor:"pointer",color:"#94a3b8",fontSize:12,padding:0}}>✎</button>
      </div>
    );
  }

  function save() {
    setSaving(true); setErr("");
    Promise.resolve(p.onUpdateRfcManual(f.codigo, val.trim())).then(function(){
      setEditing(false);
    }).catch(function(e){
      setErr("No se pudo guardar. Motivo: "+(e.message||"error desconocido"));
    }).finally(function(){ setSaving(false); });
  }

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",gap:6}}>
        <input value={val} onChange={function(e){setVal(e.target.value);}} disabled={saving} placeholder="RFC manual" style={{fontSize:12,padding:"3px 6px",border:"1px solid #cbd5e1",borderRadius:6,width:130}}/>
        <button onClick={save} disabled={saving} style={{background:"none",border:"none",cursor:saving?"default":"pointer",color:BRAND,fontSize:11,fontWeight:700,padding:0}}>{saving?"...":"Guardar"}</button>
        <button onClick={function(){ setEditing(false); setErr(""); }} disabled={saving} style={{background:"none",border:"none",cursor:saving?"default":"pointer",color:"#94a3b8",fontSize:11,padding:0}}>Cancelar</button>
      </div>
      {err?<div style={{fontSize:10,color:"#dc2626",marginTop:4}}>{err}</div>:null}
    </div>
  );
}

function AC02Table(p) {
  var d = p.data;
  if (!p.cuentaPadre) {
    return (
      <div style={{textAlign:"center",padding:"40px 20px",color:"#94a3b8",fontSize:13,background:"#fff",border:"1px solid #e2e8f0",borderRadius:10}}>
        Configura primero la cuenta padre de Clientes (boton "Configuracion" arriba) para poder calcular esta cedula.
      </div>
    );
  }
  if (d.cuentasCliente.length===0) {
    return (
      <div style={{textAlign:"center",padding:"40px 20px",color:"#94a3b8",fontSize:13,background:"#fff",border:"1px solid #e2e8f0",borderRadius:10}}>
        La cuenta padre configurada no tiene cuentas hijas (cuentas de cliente) en el catalogo.
      </div>
    );
  }
  return (
    <Card sx={{padding:0}}>
      <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
          <thead>
            <tr style={{borderBottom:"1px solid #e2e8f0",background:"#f8fafc"}}>
              <th style={thSx}>RFC</th>
              <th style={thSx}>Cuenta</th>
              <th style={thSx}>Nombre</th>
              <th style={thRightSx}>Contabilidad</th>
              <th style={thRightSx}>SAT</th>
              <th style={thRightSx}>Variacion</th>
            </tr>
          </thead>
          <tbody>
            {d.filas.map(function(f){
              return (
                <tr key={f.codigo} style={{borderBottom:"1px solid #f0f0f0"}}>
                  <td style={tdSx}><AC02RfcCell f={f} onUpdateRfcManual={p.onUpdateRfcManual}/></td>
                  <td style={tdSx}>{formatCuentaConGuiones(f.codigo)}</td>
                  <td style={tdSx}>{f.nombre}</td>
                  <td style={tdRightSx}>{toMXN(f.contab)}</td>
                  <td style={tdRightSx}>{toMXN(f.sat)}</td>
                  <td style={tdRightSx}>{toMXN(f.variacion)}</td>
                </tr>
              );
            })}
            <tr style={{borderTop:"2px solid #cbd5e1",background:"#f8fafc"}}>
              <td style={tdBoldSx} colSpan={3}>Total Cedula</td>
              <td style={tdRightBoldSx}>{toMXN(d.totalCedula.contab)}</td>
              <td style={tdRightBoldSx}>{toMXN(d.totalCedula.sat)}</td>
              <td style={tdRightBoldSx}>{toMXN(d.totalCedula.variacion)}</td>
            </tr>
            <tr style={{background:"#f8fafc"}}>
              <td style={tdBoldSx} colSpan={3}>Segun Balance</td>
              <td style={tdRightBoldSx}>{toMXN(d.segunBalance.contab)}</td>
              <td style={tdRightBoldSx}>{toMXN(d.segunBalance.sat)}</td>
              <td style={tdRightBoldSx}>{toMXN(d.segunBalance.variacion)}</td>
            </tr>
            <tr style={{background:"#f1f5f9"}}>
              <td style={tdBoldSx} colSpan={3}>Variacion</td>
              <td style={tdRightBoldSx} title={d.variacionFinal.contab!==0?"Hay cuentas de cliente que quedaron fuera del agrupamiento: la suma de las filas mostradas no coincide con la suma independiente de todas las cuentas de cliente en el periodo.":""}>{toMXN(d.variacionFinal.contab)}</td>
              <td style={tdRightBoldSx} title={d.variacionFinal.sat!==0?("Hay "+d.sinCuentaCliente.length+" factura(s) de ingresos emitida(s) en el periodo (total "+toMXN(d.sinCuentaClienteTotal)+") que no lograron emparejarse con ninguna cuenta de cliente por nombre."):""}>{toMXN(d.variacionFinal.sat)}</td>
              <td style={tdRightBoldSx}>{toMXN(d.variacionFinal.variacion)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function buildAC02PdfRows(d) {
  var rows = d.filas.map(function(f){
    var rfcCol;
    if (f.flag==="rfc_manual_sin_cfdis") rfcCol = f.rfc+" (sin CFDIs)";
    else rfcCol = ac02FlagLabel(f.flag) || (f.rfc||"—");
    return [
      rfcCol,
      formatCuentaConGuiones(f.codigo), f.nombre,
      toMXN(f.contab), toMXN(f.sat), toMXN(f.variacion),
    ];
  });
  rows.push(["", "", "Total Cedula", toMXN(d.totalCedula.contab), toMXN(d.totalCedula.sat), toMXN(d.totalCedula.variacion)]);
  rows.push(["", "", "Segun Balance", toMXN(d.segunBalance.contab), toMXN(d.segunBalance.sat), toMXN(d.segunBalance.variacion)]);
  rows.push(["", "", "Variacion", toMXN(d.variacionFinal.contab), toMXN(d.variacionFinal.sat), toMXN(d.variacionFinal.variacion)]);
  return rows;
}

function AC02Cedula(p) {
  var pdfDisabled = !p.cuentaPadre || !p.cuentas.some(function(c){ return c.subcuentaDe===p.cuentaPadre; });

  function buildPdf(desde, hasta) {
    var d = computeAC02(p.cuentas, p.movimientos, p.cfdis, p.cuentaPadre, p.cuentaTope, desde, hasta);
    return {columns:["RFC","Cuenta","Nombre","Contabilidad","SAT","Variacion"], rows: buildAC02PdfRows(d), numericColumns:[3,4,5]};
  }

  return (
    <CedulaFrame clientName={p.clientName} codigo="AC.02" nombre="Conciliacion de CFDI's Emitidos vs Contabilidad" buildPdf={buildPdf} pdfDisabled={pdfDisabled} logoDataUrl={p.logoDataUrl}>
      {function(desde, hasta){
        var data = computeAC02(p.cuentas, p.movimientos, p.cfdis, p.cuentaPadre, p.cuentaTope, desde, hasta);
        return <AC02Table data={data} cuentaPadre={p.cuentaPadre} onUpdateRfcManual={p.onUpdateRfcManual}/>;
      }}
    </CedulaFrame>
  );
}

// ── Modulo Cedulas: segundo nivel de sub-pestanas ───────────────────────────

var CEDULAS_DEF = [
  {k:"AC02", codigo:"AC.02", nombre:"Conciliacion de CFDI's Emitidos vs Contabilidad"},
];

export function CedulasModule(p) {
  var [activeCedula, setActiveCedula] = useState(CEDULAS_DEF[0].k);
  var [cfdis, setCfdis] = useState([]);
  var [loadedCfdis, setLoadedCfdis] = useState(false);
  var [config, setConfig] = useState({cuentaPadreClientes:"", cuentaTopeClientes:""});
  var [loadedConfig, setLoadedConfig] = useState(false);
  var [showConfig, setShowConfig] = useState(false);
  var [savingConfig, setSavingConfig] = useState(false);
  var [configError, setConfigError] = useState("");

  useEffect(function(){
    setLoadedCfdis(false);
    Promise.all([
      apiFetch("/catalogs/"+p.uid+"/cfdi_config").then(function(r){return r.json();}),
      apiFetch("/catalogs/"+p.uid+"/cfdis").then(function(r){return r.json();}),
    ]).then(function(results){
      var cfgD=results[0], cfdiD=results[1];
      var rfc = (cfgD.data&&cfgD.data.rfc)||"";
      setCfdis(cfdiD.data&&cfdiD.data.length ? classifyCFDIs(cfdiD.data, rfc) : []);
      setLoadedCfdis(true);
    }).catch(function(){ setLoadedCfdis(true); });
  },[p.uid]);

  useEffect(function(){
    setLoadedConfig(false);
    apiFetch("/catalogs/"+p.uid+"/cedulas_config").then(function(r){return r.json();}).then(function(d){
      setConfig(d.data&&d.data.cuentaPadreClientes!==undefined ? d.data : {cuentaPadreClientes:"", cuentaTopeClientes:""});
      setLoadedConfig(true);
    }).catch(function(){ setLoadedConfig(true); });
  },[p.uid]);

  function saveConfig(next) {
    setSavingConfig(true); setConfigError("");
    apiFetch("/catalogs/"+p.uid+"/cedulas_config",{method:"POST",body:JSON.stringify({data:next})}).then(function(r){
      if (!r.ok) {
        return r.json().catch(function(){return {};}).then(function(d){
          throw new Error(d.error || ("El servidor rechazo el guardado (HTTP "+r.status+")."));
        });
      }
      setConfig(next);
      setShowConfig(false);
    }).catch(function(err){
      console.error("[CEDULAS-CONFIG] No se pudo guardar la configuracion:", err);
      setConfigError("No se pudo guardar. Motivo: "+(err.message||"error desconocido"));
    }).finally(function(){ setSavingConfig(false); });
  }

  var loaded = loadedCfdis && loadedConfig;

  var cedulaTabs = CEDULAS_DEF.map(function(c){
    return (
      <button key={c.k} onClick={function(){setActiveCedula(c.k);}} style={{padding:"8px 14px",border:"none",background:"none",cursor:"pointer",fontSize:12,fontWeight:700,color:activeCedula===c.k?BRAND:"#94a3b8",borderBottom:activeCedula===c.k?"2px solid "+BRAND:"2px solid transparent",marginBottom:-1}}>
        {c.codigo}
      </button>
    );
  });

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",borderBottom:"1px solid #e2e8f0",marginBottom:20}}>
        <div style={{display:"flex",gap:4}}>{cedulaTabs}</div>
        <button onClick={function(){setShowConfig(true);}} style={{marginBottom:8,background:"none",border:"1px solid #dde3f0",borderRadius:8,padding:"6px 12px",fontSize:12,fontWeight:600,color:BRAND,cursor:"pointer"}}>Configuracion</button>
      </div>

      {showConfig?(
        <CedulasConfigModal
          cuentas={p.cuentas}
          config={config}
          saving={savingConfig}
          error={configError}
          onCancel={function(){setShowConfig(false); setConfigError("");}}
          onSave={saveConfig}
        />
      ):null}

      {!loaded?(
        <div style={{textAlign:"center",padding:"40px 0",color:"#94a3b8",fontSize:13}}>Cargando cedulas...</div>
      ):(
        activeCedula==="AC02"?(
          <AC02Cedula clientName={p.clientName} cuentas={p.cuentas} movimientos={p.movimientos} cfdis={cfdis} cuentaPadre={config.cuentaPadreClientes} cuentaTope={config.cuentaTopeClientes||config.cuentaPadreClientes} onUpdateRfcManual={p.onUpdateRfcManual} logoDataUrl={config.logoDataUrl}/>
        ):null
      )}
    </div>
  );
}
